import { TextField, MenuItem, Grid } from "@mui/material";
import React, { useState, useRef } from "react";
import { styled } from "@mui/material/styles";
import { getPointIcons } from "./getPointIcons";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createOrUpdateShape } from "orion-components/GlobalData/Actions";
import { getDir } from "orion-components/i18n/Config/selector";
import { Translate } from "orion-components/i18n";
import SpriteIcon from "orion-components/SharedComponents/SpriteIcon/SpriteIcon";
import ColorFrames from "orion-components/SharedComponents/EntityIcons/ColorFrames";
import Divider from "orion-components/SharedComponents/EntityIcons/Divider";
import IconsWidget from "orion-components/SharedComponents/EntityIcons/IconsWidget";
import { getEntityIconsByGroupForPoints } from "orion-components/GlobalData/Selectors";
import { getEntityFrameIconsByGroupName } from "../../../../../GlobalData/EntityIcons/selectors";

const CustomizedTextField = styled(TextField)({
	"& label": {
		color: "#9A9FA3",
		fontSize: 12
	},
	"& label.Mui-focused": {
		color: "#9A9FA3"
	},
	"& .MuiOutlinedInput-root": {
		fontSize: 12,
		color: "#FFFFFF",
		fontFamily: "Roboto",
		"& fieldset": {
			borderColor: "#5E6571"
		},
		"&:hover fieldset": {
			borderColor: "#4DB5F4"
		},
		"&.Mui-focused fieldset": {
			borderColor: "#4DB5F4"
		}
	}
});
//Points created with the old shape editor's shape panel didn't have a symbol category property, so the function below is used to overcome that issue.
const getSelectedSymbolCategory = (collectionData, imageName) => {
	const basicImages = collectionData["Basic"];
	if (basicImages.find((element) => element.name === imageName)) {
		return "Basic";
	} else {
		return "Incidents";
	}
};

const SymbolSelector = ({ handleIconAndFrameChange, iconName, defaultColor }) => {
	const dispatch = useDispatch();

	const baseMap = useSelector((state) => state.mapState.baseMap);
	const mapTools = useSelector((state) => state.mapState.mapTools);
	const frameIcons = useSelector((state) => getEntityIconsByGroupForPoints(state)("frame_icons", "group"));
	const dir = useSelector((state) => getDir(state));

	const [symbolCat, setSymbolCat] = useState("Basic");
	const [collections, setCollections] = useState([]);
	const [selectedSymbol, setSelectedSymbol] = useState("");
	const [isMapStylesLoaded, setIsMapStylesLoaded] = useState(false);

	const incidentIcons = useSelector((state) => getEntityFrameIconsByGroupName(state)("incidentSymbol"));

	const iconColor = useRef("red");

	useEffect(() => {
		if (defaultColor) iconColor.current = defaultColor;
	}, [defaultColor]);

	useEffect(() => {
		if (isMapStylesLoaded) {
			const symbolCollections = getPointIcons(symbolCat, incidentIcons);
			setCollections(symbolCollections[symbolCat]);
		}
	}, [isMapStylesLoaded]);

	const handleChangeSymbolCollection = (value) => {
		const symbolCollections = getPointIcons(value, incidentIcons);
		setSymbolCat(value);
		dispatch(createOrUpdateShape("point", { symbolCategory: value }));
		setCollections(symbolCollections[value]);
	};

	useEffect(() => {
		if (!isMapStylesLoaded && baseMap) {
			const map = baseMap.mapRef;

			const handleStyleData = () => {
				setIsMapStylesLoaded(true);
			};

			map.on("styledata", handleStyleData);

			return () => {
				map.off("styledata", handleStyleData);
			};
		}
	}, [baseMap, isMapStylesLoaded, setIsMapStylesLoaded]);

	useEffect(() => {
		if (isMapStylesLoaded) {
			const { feature } = mapTools;
			if (feature) {
				const { properties } = feature;
				const { symbol } = properties;
				let symbolCategory = null;
				let symbolCollections = [];

				if (properties?.symbolCategory) {
					symbolCategory = properties.symbolCategory;
					symbolCollections = getPointIcons(symbolCategory, incidentIcons);
				} else {
					symbolCollections = getPointIcons(symbolCat, incidentIcons);
					symbolCategory = getSelectedSymbolCategory(symbolCollections, properties.symbol);
				}

				if (symbol) {
					setCollections(symbolCollections[symbolCategory]);
					setSymbolCat(symbolCategory);
					if (symbolCategory === "Incident") {
						setSelectedSymbol(symbol);
					}
				}

				dispatch(createOrUpdateShape("point", { symbolCategory, symbol }));
			}
		}
	}, [isMapStylesLoaded]);

	const handleIconClick = (name, color) => {
		const key = `frame_circle_map_${color}`;
		setSelectedSymbol(name + key);
		dispatch(
			createOrUpdateShape("point", {
				symbol: name + "/" + key,
				symbolCategory: "Basic"
			})
		);
		handleIconAndFrameChange(name, "Basic", key);
		iconColor.current = color;
	};

	const styles = {
		pointSelection: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			backgroundColor: "#2C2D2F",
			...(dir === "ltr" && { marginRight: 20 }),
			...(dir === "rtl" && { marginLeft: 20 })
		}
	};
	return (
		<div
			style={{
				marginTop: 51,
				...(dir === "ltr" && { marginLeft: 20 }),
				...(dir === "rtl" && { marginRight: 20 })
			}}
		>
			<div style={{ width: "100%", marginBottom: 36 }}>
				<p
					style={{
						fontSize: 11,
						fontFamily: "Roboto",
						letterSpacing: 0,
						color: "#9A9FA3",
						opacity: "1",
						textAlign: dir === "ltr" ? "left" : "right",
						marginBottom: 5.5
					}}
				>
					<Translate value="global.sharedComponents.shapeEditor.components.pointEditor.components.symbolSelector.chooseSymbol" />
				</p>
				<CustomizedTextField
					variant="outlined"
					name="symbol"
					value={symbolCat}
					onChange={(e) => {
						handleChangeSymbolCollection(e.target.value);
					}}
					sx={{ width: 190 }}
					inputProps={{
						style: { width: 240, height: 34 }
					}}
					select
				>
					<MenuItem value="Incident">
						<Translate value="global.sharedComponents.shapeEditor.components.pointEditor.components.symbolSelector.incidentSymbols" />
					</MenuItem>
					<MenuItem value="Basic">
						<Translate value="global.sharedComponents.shapeEditor.components.pointEditor.components.symbolSelector.basicSymbols" />
					</MenuItem>
				</CustomizedTextField>
			</div>
			<div>
				{symbolCat === "Incident" ? (
					<Grid container spacing={2}>
						{collections?.map((symbolData) => {
							const { name } = symbolData;
							return (
								<Grid item key={name} xs={1.65}>
									<div
										onClick={() => {
											setSelectedSymbol(name);
											dispatch(
												createOrUpdateShape("point", {
													symbol: name,
													symbolCategory: symbolCat
												})
											);
											handleIconAndFrameChange(name, "Incident");
										}}
										style={{
											width: 52,
											height: 52,
											border: name === selectedSymbol ? "1px solid #FFFFFF" : null,
											opacity: 1,
											display: "flex",
											justifyContent: "center",
											alignItems: "center",
											cursor: "pointer"
										}}
									>
										<SpriteIcon iconName={name} customWidth="41px" customHeight="41px" />
									</div>
								</Grid>
							);
						})}
					</Grid>
				) : (
					<>
						<div style={styles.pointSelection}>
							<div>
								<div style={{ color: "#fff", fontSize: 11, marginTop: 15 }}>
									<Translate value="global.sharedComponents.shapeEditor.components.pointEditor.components.symbolSelector.chooseBGColor" />
								</div>
								<div style={{ display: "flex", flexWrap: "wrap", margin: "8px 0px" }}>
									<ColorFrames
										handleColorSelection={(id) => handleIconClick(iconName, id)}
										pickedColor={iconColor.current}
									/>
								</div>
								<Divider />
								<div style={{ color: "#fff", fontSize: 11, marginTop: 15 }}>
									<Translate value="global.sharedComponents.shapeEditor.components.pointEditor.components.symbolSelector.chooseSymbol" />
								</div>
								<IconsWidget
									handleIconClick={(group, name) => handleIconClick(name, iconColor.current)}
									// pickedGroup={selectedType?.group}
									frameIcons={frameIcons}
									pickedIcon={iconName}
								/>
							</div>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default SymbolSelector;
